import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '@/contexts/AuthContext';
import { uploadAvatar as uploadAvatarSvc, setAvatarUrl, removeAvatar as removeAvatarSvc } from '@/services/profiles.service';
import { sanitizeSupabaseError } from '@/lib/errorHandler';
import { validarFicheiroAvatar } from '@/lib/avatar-validacao';

/**
 * Mutações da foto de perfil (carregar / remover). Após sucesso refresca o
 * profile no AuthContext para o avatar refletir em todo o lado (UserMenu, etc.).
 * A validação de tipo/tamanho do ficheiro vive aqui (não no componente) — o componente apenas chama `uploadAvatar(file)`.
 */
export function useAvatar() {
  const { t } = useTranslation();
  const { profile, refreshProfile } = useAuthContext();

  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (!profile) throw new Error('no-profile');
      const url = await uploadAvatarSvc({ userId: profile.user_id, file });
      await setAvatarUrl({ avatarUrl: url });
    },
    onSuccess: async () => {
      await refreshProfile();
      toast.success(t('myProfile.avatar.uploadedToast'));
    },
    onError: (error) => toast.error(sanitizeSupabaseError(error)),
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (!profile) throw new Error('no-profile');
      await removeAvatarSvc({ userId: profile.user_id, avatarUrl: profile.avatar_url ?? null });
    },
    onSuccess: async () => {
      await refreshProfile();
      toast.success(t('myProfile.avatar.removedToast'));
    },
    onError: (error) => toast.error(sanitizeSupabaseError(error)),
  });

  const uploadAvatar = (file: File) => {
    const r = validarFicheiroAvatar(file);
    if (r === 'tipo') { toast.error(t('myProfile.avatar.invalidType')); return; }
    if (r === 'tamanho') { toast.error(t('myProfile.avatar.tooLarge')); return; }
    upload.mutate(file);
  };

  return {
    uploadAvatar,
    removeAvatar: () => remove.mutate(),
    isUploading: upload.isPending,
    isRemoving: remove.isPending,
  };
}
